import React, { ReactNode } from 'react'
import { NavigateFn } from '@reach/router'
import { Card, Table, Flex } from '@ui'
import { UseProjectionsHook } from './hooks/useProjections'
import { IProjectionImport, IProjectionImportDetail } from '@api'
import { pluralize } from '@utils'
import { Button, ButtonGroup, Classes } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import track from '@track'
import Event from '@track/Event'
import { DateUtil } from '@utils'
import { getImportInfo } from './utils'
import css from './tableView.module.scss'

interface TableViewProps {
  projections: UseProjectionsHook
  navigate?: NavigateFn
}

export default function TableView(props: TableViewProps) {
  const { projections } = props
  const NA = 'N/A'

  const columns = projections.detail
    ? [
        {
          label: 'Building Code',
          render: (projection: IProjectionImportDetail) => projection.buildingCode || NA,
        },
        {
          label: 'Department Name',
          render: (projection: IProjectionImportDetail) => projection.departmentName || NA,
        },
        {
          label: 'Quarter/Year',
          render: (projection: IProjectionImportDetail) =>
            `Q${DateUtil(projection.date).format('Q YYYY')}`,
        },
        {
          label: 'Row Number',
          style: { textAlign: 'right' },
          render: (projection: IProjectionImportDetail) => projection.rowNum || NA,
        },
        {
          label: 'Created By',
          render: (projection: IProjectionImportDetail) => projection.importedBy || NA,
        },
        {
          label: 'Error Message',
          render: (projection: IProjectionImportDetail) => (
            <Tooltip2 content={projection.error}>{projection.error || NA}</Tooltip2>
          ),
        },
      ]
    : [
        {
          label: 'Import',
          render: (projection: IProjectionImport) => (
            <ImportLink data={projection} select={projections.selectDetail}></ImportLink>
          ),
        },
        {
          label: 'Total',
          style: { textAlign: 'right' },
          render: (projection: IProjectionImport) => (
            <span data-test="projection_total_count">{projection.totalCount}</span>
          ),
        },
        {
          label: 'Created',
          style: { textAlign: 'right' },
          render: (projection: IProjectionImport) => (
            <span data-test="projection_created">{projection.createdCount}</span>
          ),
        },
        {
          label: 'Skipped',
          style: { textAlign: 'right' },
          render: (projection: IProjectionImport) => (
            <span data-test="projection_skipped">{projection.skippedCount}</span>
          ),
        },
        {
          label: 'Updated',
          style: { textAlign: 'right' },
          render: (projection: IProjectionImport) => (
            <span data-test="projection_updated">{projection.updatedCount}</span>
          ),
        },
        {
          label: 'Failed',
          style: { textAlign: 'right' },
          render: (projection: IProjectionImport) => (
            <span data-test="projection_count">{projection.failedCount}</span>
          ),
        },
        {
          label: 'Status',
          render: (projection: IProjectionImport) => (
            <span data-test="projection_status">{projection.status || NA}</span>
          ),
        },
      ]

  if (projections.detail && projections.tab === 'floor') {
    columns.splice(1, 0, {
      label: 'Floor Code',
      render: (projection: IProjectionImportDetail) => projection.floorCode || NA,
    })
  }

  const items = projections.detail
    ? getProjectionErrorItems([projections.detail])
    : projections.items

  return (
    <Card>
      <Card.Header>
        <Toolbar
          {...{
            projections,
            items,
          }}
        />
      </Card.Header>
      <div className={css.table_wrapper}>
        <Table
          isLoading={projections.isLoading && !projections.isSorting && !projections.isPaging}
          columns={columns}
          data={items}
          page={{
            hasNextPage: projections.pageInfo.hasNextPage,
            next: () => {
              projections.nextPage()
              track.projections({
                eventName: Event.PAGE,
              })
            },
            total: projections.count,
            isPaging: projections.isPaging,
            endCursor: '',
          }}
          headerTop={57}
        />
      </div>
    </Card>
  )
}

function Toolbar({
  projections,
  items,
}: {
  projections: UseProjectionsHook
  items: IProjectionImport[] | IProjectionImportDetail[]
}) {
  const helperText = `${items.length} ${pluralize(projections.count, 'import')}`
  let clearButton: ReactNode = null

  clearButton = projections.detail ? null : (
    <ButtonGroup fill={true} style={{ marginTop: 5 }}>
      <Button
        active={projections.tab === 'building'}
        text="Building"
        onClick={() => projections.changeTab('building')}
      />
      <Button
        active={projections.tab === 'floor'}
        text="Floor"
        onClick={() => projections.changeTab('floor')}
      />
    </ButtonGroup>
  )

  return (
    <>
      <Flex align="center" justify="space-between" style={{ width: '98%' }}>
        <Flex align="baseline" style={{ marginLeft: 10, width: '100%' }}>
          <span style={{ marginRight: 4 }}>
            {projections.isLoading ? <Skeleton /> : helperText}
          </span>
        </Flex>
        <Flex>{clearButton}</Flex>
      </Flex>
    </>
  )
}

function Skeleton() {
  return <span className={Classes.SKELETON}>Placeholder text</span>
}

function getProjectionErrorItems(data: IProjectionImport[]): IProjectionImportDetail[] {
  return data.reduce((a, { importedBy, failed, failedCount }) => {
    return failedCount > 0 && failed
      ? [
          ...a,
          ...failed.reduce((af: IProjectionImportDetail[], f) => {
            return [
              ...af,
              ...(f.errors.building || f.errors.floor || f.errors.department
                ? [
                    {
                      ...f.object,
                      importedBy,
                      error: `${formatErrorMsg(f.errors.building)} ${formatErrorMsg(f.errors.floor)}
                      ${formatErrorMsg(f.errors.department)}`,
                    },
                  ]
                : []),
            ] as unknown as IProjectionImportDetail[]
          }, []),
        ]
      : a
  }, [] as IProjectionImportDetail[])
}

function ImportLink({
  data,
  select,
}: {
  data: IProjectionImport
  select: (detail: IProjectionImport) => void
}) {
  return (
    <div
      data-test="projection_import_link"
      style={{ cursor: 'pointer' }}
      onClick={() => {
        select(data)
      }}
    >
      <Tooltip2 content="Go to Import Details">
        <div className={css.hover_underline}>{getImportInfo(data)}</div>
      </Tooltip2>
    </div>
  )
}

const formatErrorMsg = (msg: string | undefined) =>
  (msg && msg.charAt(0).toUpperCase() + msg.slice(1) + '.') || ''
